import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, X, Globe } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

export const LocationPreferences = ({ value, onChange }) => {
  const [opts, setOpts] = useState(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => { axios.get(`${API}/locations/options`, authHeaders()).then((r) => setOpts(r.data)).catch(() => {}); }, []);

  const prefs = value.preferred_locations || [];
  const anywhere = prefs.some((p) => p.type === 'anywhere');

  const addPref = (p) => {
    setNonce((n) => n + 1);
    if (!p || prefs.some((x) => x.type === p.type && x.value === p.value)) return;
    onChange({ preferred_locations: [...prefs.filter((x) => x.type !== 'anywhere'), p] });
  };
  const removePref = (i) => onChange({ preferred_locations: prefs.filter((_, idx) => idx !== i) });
  const toggleAnywhere = () => {
    if (anywhere) onChange({ preferred_locations: prefs.filter((p) => p.type !== 'anywhere') });
    else onChange({ preferred_locations: [{ type: 'anywhere', value: 'anywhere', label: 'Open to anywhere' }] });
  };

  return (
    <div className="border border-rose-100 rounded-xl p-4 bg-rose-50/30 space-y-4" data-testid="location-preferences">
      <div className="flex items-center gap-2">
        <Globe className="w-4 h-4 text-rose-500" />
        <Label className="text-rose-900 font-semibold">Preferred locations for matches</Label>
      </div>
      <p className="text-xs text-gray-500 -mt-2">Where are you open to meeting someone? Add regions or countries — e.g. you're in England but open to the US or West Africa.</p>

      {/* current chips */}
      <div className="flex flex-wrap gap-2" data-testid="pref-loc-chips">
        <Badge onClick={toggleAnywhere} data-testid="pref-anywhere"
          className={`cursor-pointer ${anywhere ? 'bg-rose-600' : 'bg-white text-gray-500 border border-gray-200'}`}>
          🌍 Open to anywhere
        </Badge>
        {prefs.filter((p) => p.type !== 'anywhere').map((p, i) => (
          <Badge key={`${p.type}-${p.value}`} className="bg-rose-100 text-rose-700 gap-1" data-testid={`pref-chip-${p.value}`}>
            <MapPin className="w-3 h-3" /> {p.label}
            <button onClick={() => removePref(prefs.indexOf(p))} className="ml-1"><X className="w-3 h-3" /></button>
          </Badge>
        ))}
      </div>

      {!anywhere && opts && (
        <div className="grid sm:grid-cols-2 gap-2" key={nonce}>
          <Select onValueChange={(v) => { const r = opts.regions.find((x) => x.value === v); addPref(r); }}>
            <SelectTrigger className="bg-white h-9" data-testid="pref-add-region"><SelectValue placeholder="+ Add a region" /></SelectTrigger>
            <SelectContent>{opts.regions.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select onValueChange={(v) => { const c = opts.countries.find((x) => x.value === v); addPref(c); }}>
            <SelectTrigger className="bg-white h-9" data-testid="pref-add-country"><SelectValue placeholder="+ Add a country" /></SelectTrigger>
            <SelectContent className="max-h-64">{opts.countries.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3 pt-1">
        <div>
          <Label className="text-xs text-gray-500">Open to long distance?</Label>
          <Select value={value.open_to_long_distance ? 'yes' : 'no'} onValueChange={(v) => onChange({ open_to_long_distance: v === 'yes' })}>
            <SelectTrigger className="bg-white h-9 mt-1" data-testid="pref-longdistance"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Willing to relocate?</Label>
          <Select value={value.willing_to_relocate || ''} onValueChange={(v) => onChange({ willing_to_relocate: v })}>
            <SelectTrigger className="bg-white h-9 mt-1" data-testid="pref-relocate"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="maybe">For the right person</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Who would move?</Label>
          <Select value={value.relocation_who || ''} onValueChange={(v) => onChange({ relocation_who: v })}>
            <SelectTrigger className="bg-white h-9 mt-1" data-testid="pref-who"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent><SelectItem value="me">I would</SelectItem><SelectItem value="them">They would</SelectItem><SelectItem value="either">Either of us</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default LocationPreferences;
